/**
 * Hex / RGB / HSV conversions for the org branding color palette (pure functions).
 * H in degrees [0, 360), S and V in [0, 1].
 */

const HEX_6 = /^#?([0-9A-Fa-f]{6})$/
const HEX_3 = /^#?([0-9A-Fa-f]{3})$/

export interface Rgb {
  r: number
  g: number
  b: number
}

export interface Hsv {
  h: number
  s: number
  v: number
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n))
}

function expandShortHex(short: string): string {
  const a = short[0]
  const b = short[1]
  const c = short[2]
  return `${a}${a}${b}${b}${c}${c}`
}

/** Returns normalized #RRGGBB or null if invalid. */
export function normalizeHexColor(input: string): string | null {
  const t = String(input).trim()
  if (!t) return null
  const m6 = t.match(HEX_6)
  if (m6) return `#${m6[1].toUpperCase()}`
  const m3 = t.match(HEX_3)
  if (m3) return `#${expandShortHex(m3[1]).toUpperCase()}`
  return null
}

export function parseHexToRgb(hex: string): Rgb | null {
  const n = normalizeHexColor(hex)
  if (!n) return null
  const h = n.slice(1)
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

export function rgbToHex(r: number, g: number, b: number): string {
  const to = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase()
}

export function rgbToHsv(r: number, g: number, b: number): Hsv {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const d = max - min
  let h = 0
  if (d !== 0) {
    if (max === rn) {
      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6
    } else if (max === gn) {
      h = ((bn - rn) / d + 2) / 6
    } else {
      h = ((rn - gn) / d + 4) / 6
    }
  }
  const s = max === 0 ? 0 : d / max
  const v = max
  return { h: h * 360, s, v }
}

export function hsvToRgb(h: number, s: number, v: number): Rgb {
  const hh = ((((h % 360) + 360) % 360) / 360) * 6
  const i = Math.floor(hh)
  const f = hh - i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)
  let rn = 0
  let gn = 0
  let bn = 0
  switch (i % 6) {
    case 0:
      rn = v
      gn = t
      bn = p
      break
    case 1:
      rn = q
      gn = v
      bn = p
      break
    case 2:
      rn = p
      gn = v
      bn = t
      break
    case 3:
      rn = p
      gn = q
      bn = v
      break
    case 4:
      rn = t
      gn = p
      bn = v
      break
    case 5:
      rn = v
      gn = p
      bn = q
      break
    default:
      break
  }
  return {
    r: Math.round(rn * 255),
    g: Math.round(gn * 255),
    b: Math.round(bn * 255),
  }
}

export function hexToHsv(hex: string): Hsv | null {
  const rgb = parseHexToRgb(hex)
  if (!rgb) return null
  return rgbToHsv(rgb.r, rgb.g, rgb.b)
}

export function hsvToHex(h: number, s: number, v: number): string {
  const { r, g, b } = hsvToRgb(h, clamp01(s), clamp01(v))
  return rgbToHex(r, g, b)
}

/** Fully saturated bright RGB for hue strip (S=1, V=1). */
export function hueToPureHex(h: number): string {
  return hsvToHex(h, 1, 1)
}
